import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Empty } from "@/components/KpiCard";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { CATEGORIES, fmtDate, todayISO } from "@/lib/constants";

const TSTATUS = { concluido: "Concluído", em_andamento: "Em andamento", planejado: "Planejado" };

const TreatmentDialog = ({ open, onOpenChange, patientId }) => {
  const api = useApi();
  const [f, setF] = useState({ date: todayISO(), category: "Avaliação", teeth: "", description: "", status: "concluido" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const teeth = f.teeth.split(/[,\s]+/).filter(Boolean);
      await api.post(`/patients/${patientId}/treatments`, { ...f, procedure: f.category, teeth });
      toast.success("Registro adicionado ao histórico");
      notifyRefresh();
      onOpenChange(false);
      setF({ date: todayISO(), category: "Avaliação", teeth: "", description: "", status: "concluido" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="treatment-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Registrar tratamento / evolução</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Data</Label><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="treatment-date-input" /></div>
            <div className="grid gap-1.5">
              <Label>Procedimento</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="treatment-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Dentes (ex.: 11, 21)</Label><Input value={f.teeth} onChange={(e) => setF({ ...f, teeth: e.target.value })} data-testid="treatment-teeth-input" /></div>
            <div className="grid gap-1.5">
              <Label>Situação</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="treatment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TSTATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5"><Label>Descrição / evolução clínica</Label><Textarea rows={4} required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="treatment-description-input" /></div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="treatment-save-btn">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const HistoryTab = ({ p, treatments }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const { data: appts } = useResource(`/appointments?patient_id=${p.id}`);
  const remove = async (id) => {
    if (!window.confirm("Excluir este registro?")) return;
    try { await api.delete(`/treatments/${id}`); notifyRefresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
      <div className="surface p-5 sm:p-6">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">Histórico de tratamentos</h3>
          <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setOpen(true)} data-testid="add-treatment-btn"><Plus className="mr-1 h-4 w-4" /> Registrar</Button>
        </div>
        {treatments.length === 0 ? <Empty testId="treatments-empty">Nenhum tratamento registrado</Empty> : (
          <ol className="relative ml-2 border-l-2 border-sky-100" data-testid="treatments-timeline">
            {treatments.map((t) => (
              <li key={t.id} className="group relative mb-6 pl-6" data-testid={`treatment-item-${t.id}`}>
                <span className={`absolute -left-[7px] top-1.5 h-3 w-3 rounded-full ring-4 ring-white ${t.status === "concluido" ? "bg-sky-500" : t.status === "em_andamento" ? "bg-amber-500" : "bg-slate-300"}`} />
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono-num text-xs text-slate-400">{fmtDate(t.date)} · {TSTATUS[t.status] || t.status}</p>
                    <p className="font-semibold text-slate-800">{t.category}{t.teeth?.length ? <span className="ml-2 text-xs font-medium text-sky-600">Dentes {t.teeth.join(", ")}</span> : null}</p>
                    <p className="mt-0.5 text-sm text-slate-600">{t.description}</p>
                  </div>
                  <button onClick={() => remove(t.id)} className="opacity-0 transition-opacity group-hover:opacity-100" data-testid={`delete-treatment-${t.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
      <div className="surface p-5">
        <h3 className="font-display mb-4 text-lg font-bold text-slate-900">Consultas</h3>
        <div className="space-y-2" data-testid="patient-appointments-list">
          {(appts || []).slice().reverse().map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2">
              <div>
                <p className="font-mono-num text-xs text-slate-500">{fmtDate(a.date)} {a.time}</p>
                <p className="text-sm font-medium text-slate-700">{a.procedure}</p>
              </div>
              <StatusBadge status={a.effective_status} />
            </div>
          ))}
          {appts && appts.length === 0 && <Empty>Sem consultas</Empty>}
        </div>
      </div>
      <TreatmentDialog open={open} onOpenChange={setOpen} patientId={p.id} />
    </div>
  );
};
